import {
  ACCEPTED_IMAGE_FILE_TYPES,
  useMarkdownImageUpload,
} from '@github-ui/agent-sessions/hooks/use-markdown-image-upload'
import {useClickFileSelect} from '@github-ui/use-unified-file-select'
import {ImageIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {type RefObject, useEffect} from 'react'

import styles from './ImageAttachButton.module.css'

export type ImageAttachButtonProps = {
  /** Ref to the textarea where uploaded image markdown will be inserted. */
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  /** Repository ID used for asset uploads. When undefined, the button is disabled. */
  repositoryId?: string
  /** Whether the button should be disabled (e.g. no repo selected). */
  disabled?: boolean
  /** Called when the upload state changes so the parent can disable submit, etc. */
  onUploadStateChange?: (isUploading: boolean) => void
}

/**
 * Self-contained image attach button that opens a native file picker on click
 * and uploads selected images, inserting markdown into the referenced textarea.
 */
export function ImageAttachButton({textAreaRef, repositoryId, disabled, onUploadStateChange}: ImageAttachButtonProps) {
  const {processFiles, isUploading} = useMarkdownImageUpload({
    textAreaRef,
    repositoryId,
  })

  const clickTargetProps = useClickFileSelect({
    onSelect: (accepted: File[]) => processFiles(accepted),
    multi: true,
    acceptedFileTypes: ACCEPTED_IMAGE_FILE_TYPES,
  })

  useEffect(() => {
    onUploadStateChange?.(isUploading)
  }, [isUploading, onUploadStateChange])

  const isInactive = disabled || isUploading

  return (
    <IconButton
      icon={ImageIcon}
      aria-label="Attach an image"
      variant="invisible"
      className={styles.attachButton}
      inactive={isInactive}
      aria-disabled={isInactive}
      {...(!isInactive ? clickTargetProps : {})}
    />
  )
}
